import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { formatEntryDate } from '../components/community/CommunityFeedItem';
import { JournalEntrySocial, communityEntryQueryKey } from '../components/community/JournalEntrySocial';
import { getCommunityEntry, toPlainParagraphs } from '../services/communityService';
import { useTranslation } from '../i18n/useTranslation';
import './CommunityEntryPage.css';

/** /community/:entryId — one public journal entry with likes and comments. */
export function CommunityEntryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { entryId = '' } = useParams<{ entryId: string }>();

  const { data: entry, isLoading, isError, refetch } = useQuery({
    queryKey: communityEntryQueryKey(entryId),
    queryFn: () => getCommunityEntry(entryId),
    enabled: Boolean(entryId),
    retry: false,
  });

  const back = (
    <Button variant="ghost" onClick={() => navigate('/community')}>
      {t('community.back')}
    </Button>
  );

  let body;
  if (isLoading) {
    body = (
      <PageHeader title={t('community.entryLoadingTitle')} back={back}>
        <LiveRegion data-testid="community-entry-loading">
          <Text color="muted">{t('community.entryLoading')}</Text>
        </LiveRegion>
      </PageHeader>
    );
  } else if (isError || !entry) {
    body = (
      <>
        <PageHeader title={t('community.entryUnavailableTitle')} back={back} />
        <LoadErrorState
          data-testid="community-entry-error"
          title={t('community.entryLoadError')}
          onRetry={() => void refetch()}
        />
      </>
    );
  } else {
    const paragraphs = toPlainParagraphs(entry.content);
    body = (
      <>
        <PageHeader
          title={entry.title}
          description={`${entry.isOwn ? t('community.yourEntry') : t('community.byMember')} · ${t('community.aquarium', {
            name: entry.aquariumName || t('community.unnamedAquarium'),
          })} · ${formatEntryDate(entry.createdAt, i18n.language)}`}
          back={back}
          actions={
            entry.isOwn ? (
              <Button variant="secondary" onClick={() => navigate(`/aquariums/${entry.aquariumId}/journal/${entry.id}`)}>
                {t('community.openInJournal')}
              </Button>
            ) : undefined
          }
        />
        {entry.visibility === 'private' && (
          <Alert tone="info" live="off">{t('community.privateNotice')}</Alert>
        )}
        <Section title={t('journal.detail.contentTitle')}>
          <Card className="community-entry-page__content" data-testid="community-entry-content">
            {paragraphs.map((paragraph, index) => (
              <Text key={index}>{paragraph}</Text>
            ))}
            {entry.tags.length > 0 && (
              <ul className="community-entry-page__tags" aria-label={t('journal.tags')}>
                {entry.tags.map((tag) => (
                  <li key={tag}><Badge tone="neutral">{tag}</Badge></li>
                ))}
              </ul>
            )}
          </Card>
        </Section>
        <Section title={t('community.reactionsTitle')}>
          <Card>
            <JournalEntrySocial entry={entry} headingLevel={3} />
          </Card>
        </Section>
      </>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default" data-testid="community-entry-page">
          {body}
        </PageContent>
      }
    />
  );
}

export default CommunityEntryPage;
